import React from 'react';
import { useWorld } from '../../contexts/WorldDataContext';
import { imageEntryForClass, imageEntryForMonster } from '../../utils/entityImages';
import { spriteForItem } from '../../utils/itemSprites';
import { staffItemImage } from '../../data/guide/itemImages';
import { EntityImage } from './EntityImage';
import { ItemThumb } from './ItemSprite';
import type { EntityRef } from '../../types/guide';
import type { WorldData } from '../../types/world';
type Size = 'thumb' | 'card' | 'hero';

/** ¿Tiene imagen esta entidad? Misma resolución por tipo + ID exacto que EntityThumb. */
export function entityHasImage(entity: EntityRef | undefined, data: WorldData | undefined): boolean {
  if (!entity) return false;
  if (entity.type === 'item') return !!staffItemImage(entity.id) || !!spriteForItem(entity.id);
  if (entity.type === 'class') return !!imageEntryForClass(entity.id)?.image;
  return !!imageEntryForMonster(data, entity.id)?.image;
}

/**
 * Imagen de cualquier entidad de la guía (ítem, variante, monstruo, NPC o clase) por tipo e ID exacto.
 * Es el único punto de entrada para buscador, tarjetas, recompensas, enlaces y fichas: si la imagen
 * funciona en una sección, funciona en todas. Ítems: celda de su lámina con el encuadre ampliado.
 * Vista previa al pasar el cursor o enfocar; el clic nunca abre nada ni activa el enlace que la contiene.
 * Sin imagen: `fallback` (p. ej. el icono de la sección) o marcador neutro con `placeholder`.
 */
export function EntityThumb({
  entity,
  name,
  size = 'thumb',
  focusable = true,
  lazy = true,
  placeholder = false,
  fallback = null
}: {
  entity?: EntityRef;
  name: string;
  size?: Size;
  focusable?: boolean;
  lazy?: boolean;
  placeholder?: boolean;
  fallback?: React.ReactNode;
}) {
  const {
    data
  } = useWorld();
  if (!entityHasImage(entity, data) && !placeholder) return <>{fallback}</>;
  if (entity?.type === 'item') return <ItemThumb id={entity.id} name={name} size={size} placeholder={placeholder} focusable={focusable} lazy={lazy} data-id="element-1663" data-mp="oak8i9gs2ni7" />;
  const entry = entity?.type === 'class' ? imageEntryForClass(entity.id) : entity?.type === 'monster' ? imageEntryForMonster(data, entity.id) : undefined;
  const person = entity?.type === 'class' || entry?.kind === 'npc';
  return <EntityImage entry={entry} alt={name} name={name} size={size} lazy={lazy} focusable={focusable} kind={entity ? person ? 'persona' : 'monstruo' : 'item'} data-id="element-1664" data-mp="2ubmko2mjgig3" />;
}